import React, { useCallback, useEffect, useState } from 'react';
import Modal from '../../common/Modal';
import Button from '../../common/Button';
import FormControl from '../../common/FormControl';
import Toast, { ToastMessage } from '../../common/Toast';
import { Save, Check, Loader2, Lock, CircleMinus, CircleCheck, Edit2 } from 'lucide-react';
import { confirmWarning, confirmActive } from '../../common/confirm';
import { useAuth } from '../../../contexts/AuthContext';
import { usePermissions } from '../../../contexts/PermissionContext';
import { useEntitySettings } from '../../../contexts/EntitySettingsContext';
import LitPositionPicker from './LitPositionPicker';
import LitTimeline from './LitTimeline';
import {
  litInstanceService, litHistoryService,
  type LitInstance, type LitHistoryEntry, type PositionLookup,
} from '../../../services/litInstanceService';
import {
  litProceedingsReadService,
  type LitProceedingGraph,
} from '../../../services/litigationMasterService';
import { governanceApplicableLawsService, type ApplicableLaw } from '../../../services/governanceService';
import jurisdictionsService, { type Jurisdiction } from '../../../services/jurisdictionsService';
import extractApiError from '../../../utils/apiError';
import { useFieldValidation } from '../../../validation/useFieldValidation';
import { ValidatedInput, ValidatedTextarea, ValidatedDatePicker } from '../../../validation/ValidatedFields';
import { isUniqueLocal } from '../../../validation/uniqueness';

type Tab = 'notice' | 'history';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
  instance: LitInstance | null;  // null = create new
  /** History/archive view — fully read-only: no save/advance/close/assign. */
  readOnly?: boolean;
  /** All loaded litigations — used for the instant in-memory notice_id uniqueness pre-check. */
  existing?: LitInstance[];
  /** Persona model. 'grc' = full edit + allocation; 'execute' = record-steps only. */
  mode?: 'grc' | 'execute';
}

interface NoticeForm {
  notice_id: string;
  notice_date: string;
  law_id: number | null;
  jurisdiction_id: number | null;
  section: string;
  due_date: string;
  amount: string;
  description: string;
}

/** Workflow is folded into the notice — selected at creation, not a separate step. */
interface WorkflowForm {
  is_free_style: boolean;
  proceeding_id: number | null;
}

const emptyNotice = (): NoticeForm => ({
  notice_id: '', notice_date: '', law_id: null, jurisdiction_id: null,
  section: '', due_date: '', amount: '', description: '',
});

const emptyWorkflow = (): WorkflowForm => ({
  is_free_style: false, proceeding_id: null,
});

/** Read-only label/value pair — used for the executor's notice view (no form boxes). */
const KV: React.FC<{ label: string; value?: string | null; full?: boolean }> = ({ label, value, full }) => (
  <div className={full ? 'col-span-2' : ''}>
    <p className="text-[11px] uppercase tracking-wide text-gray-400 font-medium mb-0.5">{label}</p>
    <p className="text-sm text-gray-800 whitespace-pre-wrap break-words">{value && value.trim() ? value : '—'}</p>
  </div>
);

/**
 * Bordered enclosure with its label sitting on the top border — the HTML
 * <fieldset>/<legend> pattern (a.k.a. "legend on the border" group box).
 * Children render in a 2-column grid; use `col-span-2` for full-width fields.
 */
const Section: React.FC<{ title: React.ReactNode; children: React.ReactNode }> = ({ title, children }) => (
  <fieldset className="field-group">
    <legend className="field-group-legend">
      {title}
    </legend>
    <div className="field-group-body field-group-body--grid">
      {children}
    </div>
  </fieldset>
);

const LitInstanceDetail: React.FC<Props> = ({ isOpen, onClose, onSaved, instance, readOnly = false, existing = [], mode = 'grc' }) => {
  const { formatDate } = useEntitySettings();
  const { state: authState } = useAuth();
  const currentUserId = authState.user?.id ?? null;

  const vh = useFieldValidation();
  const isNew = !instance;
  const [activeTab, setActiveTab] = useState<Tab>('notice');
  // View-first edit: existing records open read-only until "Edit" is clicked.
  const [isEditing, setIsEditing] = useState(false);
  // Live copy of the instance: kept fresh after save / advance / close so template
  // selection, assignment and step changes reflect WITHOUT reopening the modal
  // (the `instance` prop is a stale snapshot from when the row was clicked).
  const [live,     setLive]     = useState<LitInstance | null>(instance);
  const inst = live ?? instance;
  const [notice,   setNotice]   = useState<NoticeForm>(emptyNotice());
  const [workflow, setWorkflow] = useState<WorkflowForm>(emptyWorkflow());
  const [saving,   setSaving]   = useState(false);
  const [toggling, setToggling] = useState(false);
  const [toast,    setToast]    = useState<ToastMessage | null>(null);

  // Assignment (single assignee — the exact position is picked directly)
  const [positionId,   setPositionId]   = useState<number | null>(null);
  const [resolvedPos,  setResolvedPos]  = useState<PositionLookup | null>(null);

  // Lookups: proceeding templates, applicable laws (Act), jurisdictions
  const [proceedings,   setProceedings]   = useState<{ id: number; name: string; code?: string | null }[]>([]);
  const [laws,          setLaws]          = useState<ApplicableLaw[]>([]);
  const [jurisdictions, setJurisdictions] = useState<Jurisdiction[]>([]);

  // Mandatory-field errors not handled by the text validator (template / assignment)
  const [structErr, setStructErr] = useState<string | null>(null);
  const [assignErr, setAssignErr] = useState<string | null>(null);

  // History / Execution tab
  const [history,      setHistory]      = useState<LitHistoryEntry[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [graph,        setGraph]        = useState<LitProceedingGraph | null>(null);

  // Permissions
  const { check } = usePermissions();
  const canLitigation = check('COM_LITIGATIONS', 'view+modify').allowed;
  const isCreator  = !!inst && inst.created_by === currentUserId;
  const isPrimary  = !!inst && inst.assigned_user_id === currentUserId;
  // Domain gate layered under the RBAC activity extent. The GRC maker who logged
  // the litigation (creator) always retains control; the single assignee may
  // advance steps. (Team-member model retired — one assignee per litigation.)
  const canAdvance = canLitigation && (isCreator || isPrimary);
  // History/archive overrides everything to view-only.
  const canEdit = canLitigation && !readOnly;
  const canAct  = canAdvance && !readOnly;
  // Execute persona records steps only; notice + workflow editing stay with GRC.
  const isExecute     = mode === 'execute';
  // Approved records are frozen: the Notice tab drops to read-only and the
  // Update/Inactive footer is hidden. Unlock via the status dot's Reopen action.
  const isApproved    = (inst as any)?.workflow_state === 'approved';
  const canEditNotice = canEdit && !isExecute && !isApproved;
  // View-first: the form is always rendered for the GRC-creator path, but its
  // fields stay disabled until "Edit" is clicked (new records start editable).
  const fieldsEditable = isNew || isEditing;

  // ── Reset on open ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    setActiveTab(isExecute ? 'history' : 'notice');
    setIsEditing(false);   // every open starts read-only ("Viewing")
    vh.reset();
    setLive(instance);
    setHistory([]);
    setGraph(null);
    setPositionId(null);
    setResolvedPos(null);
    setStructErr(null);
    setAssignErr(null);

    if (instance) {
      setNotice({
        notice_id:       instance.notice_id,
        notice_date:     instance.notice_date ?? '',
        law_id:          instance.law_id,
        jurisdiction_id: instance.jurisdiction_id,
        section:         instance.section ?? '',
        due_date:        instance.due_date ?? '',
        amount:          instance.amount ?? '',
        description:     instance.description ?? '',
      });
      setWorkflow({
        is_free_style: instance.is_free_style,
        proceeding_id: instance.proceeding_id,
      });
    } else {
      setNotice(emptyNotice());
      setWorkflow(emptyWorkflow());
    }
  }, [isOpen, instance, isExecute]);

  // Load lookups once per open
  useEffect(() => {
    if (!isOpen) return;
    litProceedingsReadService.getDropdown().then(setProceedings).catch(() => {});
    governanceApplicableLawsService.getDropdown().then(setLaws).catch(() => {});
    jurisdictionsService.getDropdown().then(setJurisdictions).catch(() => {});
  }, [isOpen]);

  // Load history + graph when on the history tab
  const loadHistory = useCallback(async () => {
    if (!instance) return;
    setHistoryLoading(true);
    try {
      setHistory(await litHistoryService.getAll(instance.id));
    } catch { } finally { setHistoryLoading(false); }
  }, [instance]);

  const loadGraph = useCallback(async () => {
    const cur = live ?? instance;
    if (!cur?.proceeding_id || cur.is_free_style) { setGraph(null); return; }
    try {
      setGraph(await litProceedingsReadService.getGraph(cur.proceeding_id));
    } catch { }
  }, [live, instance]);

  useEffect(() => {
    if (!isOpen || !instance) return;
    if (activeTab === 'history') { loadHistory(); loadGraph(); }
  }, [activeTab, isOpen, instance, loadHistory, loadGraph]);

  // ── Helpers ────────────────────────────────────────────────────────────────
  const n = (p: Partial<NoticeForm>)   => setNotice(prev   => ({ ...prev, ...p }));
  const w = (p: Partial<WorkflowForm>) => setWorkflow(prev => ({ ...prev, ...p }));

  // ── Save notice (notice + workflow + assignment, in one shot) ───────────────
  const handleSaveNotice = async (e: React.FormEvent) => {
    e.preventDefault();
    // Guard: only the explicit Update/Submit button may save. A stray submit
    // (e.g. Enter in a field) while still in read-only view must not hit the API.
    if (!isNew && !isEditing) return;

    // Format validation (notice_id + notice_date required; rest optional)
    const formatOk = vh.validateAll([
      { name: 'notice_id',   code: 109, value: notice.notice_id, required: true },
      { name: 'notice_date', code: 305, value: notice.notice_date, required: true },
      { name: 'due_date',    code: 305, value: notice.due_date },
      { name: 'section',     code: 109, value: notice.section },
      { name: 'description', code: 110, value: notice.description },
    ]);
    // Cross-field 601 — Date of Notice must be on or before Date of First Response.
    let crossOk = true;
    if (notice.notice_date && notice.due_date && notice.notice_date > notice.due_date) {
      vh.setError('due_date', 'Date of Notice must be on or before Date of First Response');
      crossOk = false;
    }

    // Mandatory: template (unless free-style) + an assignment.
    const needTemplate = !workflow.is_free_style && !workflow.proceeding_id;
    const hasAssignment = !!resolvedPos || !!inst?.assigned_position_id;
    setStructErr(needTemplate ? 'Select a proceeding template (or choose Free-style).' : null);
    setAssignErr(hasAssignment ? null : 'Assignment is required — pick a position.');

    if (!formatOk || !crossOk || needTemplate || !hasAssignment) return;

    // Uniqueness (entity-scoped) — case-insensitive local pre-check on notice_id (code 948).
    if (!isUniqueLocal(948, notice.notice_id, existing, instance?.id ?? undefined)) {
      vh.setError('notice_id', 'Notice / Reference ID already exists');
      return;
    }

    setSaving(true);
    try {
      const payload: Partial<LitInstance> = {
        notice_id:       notice.notice_id.trim(),
        notice_date:     notice.notice_date || undefined,
        law_id:          notice.law_id ?? undefined,
        jurisdiction_id: notice.jurisdiction_id ?? undefined,
        section:         notice.section     || undefined,
        due_date:        notice.due_date    || undefined,
        amount:          notice.amount      || undefined,
        description:     notice.description || undefined,
        // Workflow — folded into the notice itself.
        is_free_style: workflow.is_free_style,
        proceeding_id: workflow.is_free_style ? null : (workflow.proceeding_id ?? undefined),
      };
      if (resolvedPos) {
        payload.assigned_position_id = resolvedPos.id;
        payload.assigned_user_id     = resolvedPos.user_id ?? undefined;
      }
      if (isNew) {
        await litInstanceService.create(payload);
        setToast({ type: 'success', message: 'Notice logged.' });
        onSaved();
        onClose();
      } else {
        const fresh = await litInstanceService.update(instance!.id, payload);
        setLive(fresh);
        if (fresh.proceeding_id && !fresh.is_free_style) {
          try { setGraph(await litProceedingsReadService.getGraph(fresh.proceeding_id)); } catch { }
        } else {
          setGraph(null);
        }
        setToast({ type: 'success', message: 'Litigation updated.' });
        onSaved();
      }
    } catch (e: any) {
      setToast({ type: 'error', message: extractApiError(e, 'Save failed') });
    } finally { setSaving(false); }
  };

  // ── Advance step ──────────────────────────────────────────────────────────
  const handleAdvance = async (payload: Parameters<typeof litHistoryService.advance>[1]) => {
    if (!instance) return;
    await litHistoryService.advance(instance.id, payload);
    // Refresh the live instance so current_step_id updates -> the template's NEXT
    // step options recompute; then reload history + graph.
    const fresh = await litInstanceService.getById(instance.id);
    setLive(fresh);
    await loadHistory();
    if (fresh.proceeding_id && !fresh.is_free_style) {
      try { setGraph(await litProceedingsReadService.getGraph(fresh.proceeding_id)); } catch { }
    }
    onSaved();
  };

  // ── Close litigation ──────────────────────────────────────────────────────
  const handleClose = async () => {
    if (!instance) return;
    try {
      await litInstanceService.close(instance.id);
      setToast({ type: 'success', message: 'Litigation closed.' });
      onSaved();
      onClose();
    } catch (e: any) {
      setToast({ type: 'error', message: extractApiError(e, 'Failed to close') });
    }
  };

  const handleToggleActive = async () => {
    if (!inst) return;
    const goingInactive = (inst.is_active ?? 1) === 1;
    const confirmFn = goingInactive ? confirmWarning : confirmActive;
    const ok = await confirmFn({
      title: goingInactive ? 'Inactivate this litigation?' : 'Reactivate this litigation?',
      text: goingInactive
        ? 'It will be hidden from active lists until reactivated.'
        : 'It will appear in active lists again.',
      confirmText: goingInactive ? 'Yes, Inactivate' : 'Yes, Reactivate',
    });
    if (!ok) return;
    setToggling(true);
    try {
      const newActive = await litInstanceService.toggleStatus(inst.id);
      setLive({ ...inst, is_active: newActive });
      setToast({
        type: newActive === 1 ? 'success' : 'warning',
        message: newActive === 1 ? 'Litigation reactivated.' : 'Litigation inactivated.',
      });
      onSaved();
    } catch (e: any) {
      setToast({ type: 'error', message: extractApiError(e, 'Failed to toggle status') });
    } finally {
      setToggling(false);
    }
  };

  const tabs: { key: Tab; label: string; disabled?: boolean }[] = [
    { key: 'notice',   label: 'Notice' },
    { key: 'history',  label: `${isExecute ? 'Execution' : 'History'}${history.length ? ` (${history.length})` : ''}`, disabled: isNew },
  ];

  // Map a litigation status to a shared status-badge variant.
  const statusVariant = (s?: string | null): string => {
    switch (s) {
      case 'Open':        return 'danger';
      case 'In Progress': return 'warning';
      case 'Closed':      return 'neutral';
      default:            return 'neutral';
    }
  };

  const assignedDisplay = [inst?.assigned_position_name, inst?.assigned_user_name].filter(Boolean).join(' · ');

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <Modal.Header>
          {isNew
            ? 'Create Litigation'
            : isApproved
              ? <>Viewing (locked) "<span className="item-highlight">{inst?.notice_id}</span>"</>
              : (canEditNotice && isEditing)
                ? <>Editing "<span className="item-highlight">{inst?.notice_id}</span>"</>
                : <span className="flex items-center gap-2">
                    <span className="item-highlight">{inst?.notice_id}</span>
                    {inst?.status && (
                      <span className={`status-badge sm ${statusVariant(inst.status)}`}>
                        {inst.status}
                      </span>
                    )}
                  </span>
          }
        </Modal.Header>

        <Modal.Body>
          {/* Tab bar */}
          <div className="flex gap-1 border-b border-gray-200 mb-5 -mt-1">
            {tabs.map(t => (
              <button key={t.key} type="button" disabled={t.disabled}
                onClick={() => !t.disabled && setActiveTab(t.key)}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition -mb-px
                  ${activeTab === t.key
                    ? 'border-indigo-500 text-indigo-700'
                    : t.disabled
                      ? 'border-transparent text-gray-300 cursor-not-allowed'
                      : 'border-transparent text-gray-500 hover:text-gray-700 cursor-pointer'}`}
                data-testid={`TAB_lit_inst_${t.key}`}>
                {t.label}
              </button>
            ))}
          </div>

          {/* ── NOTICE TAB — read-only (executor / archive / approved) ── */}
          {activeTab === 'notice' && !canEditNotice && (
            <div className="flex flex-col gap-y-4" data-testid="VIEW_lit_inst_notice_ro">
              {isApproved && (
                <div className="approved-banner" data-testid="BANNER_lit_inst_locked">
                  <Lock size={14} /> Record is approved and locked. Reopen via the workflow button to edit.
                </div>
              )}
              <Section title="About the Notice">
                {!isNew && <KV label="Code" value={(inst as any)?.code || '—'} full />}
                <KV label="Notice / Reference ID" value={notice.notice_id} full />
                <KV label="Date of Notice" value={notice.notice_date ? formatDate(notice.notice_date) : ''} />
                <KV label="Date of First Response" value={notice.due_date ? formatDate(notice.due_date) : ''} />
              </Section>

              <Section title="Statute & Demand">
                <KV label="Act / Law" value={inst?.law_name || inst?.act} />
                <KV label="Section / Rule" value={notice.section} />
                <KV label="Jurisdiction" value={inst?.jurisdiction_name || inst?.jurisdiction_text} />
                <KV label="Demand Amount (₹)" value={notice.amount} />
                <KV label="Description" value={notice.description} full />
              </Section>

              <Section title="Structure">
                <KV label="Mode" value={workflow.is_free_style ? 'Free-style' : 'Template-based'} />
                {!workflow.is_free_style && (
                  <KV label="Proceeding Template" value={inst?.proceeding_name} />
                )}
              </Section>

              <Section title="Assignment">
                <KV label="Assigned To" value={assignedDisplay} full />
              </Section>
            </div>
          )}

          {/* ── NOTICE TAB — editable form (GRC creator); fields disabled
                 until Edit is clicked (new records start editable) ─────── */}
          {activeTab === 'notice' && canEditNotice && (
            <form id="lit-notice-form" onSubmit={handleSaveNotice}
              className="flex flex-col gap-y-4">

              {/* ── About the Notice ──────────────────────────────────── */}
              <Section title="About the Notice">
                {/* System code (LIT-N) shown in the list — read-only, prefilled.
                    Sits side-by-side with Notice / Reference ID. */}
                {!isNew && (
                  <FormControl.Input
                    label="Code"
                    value={(inst as any)?.code || '—'}
                    disabled
                    data-testid="INP_lit_inst_code" />
                )}
                <div className={isNew ? 'col-span-2' : undefined}>
                  <ValidatedInput vh={vh} name="notice_id" code={109} required
                    label="Notice / Reference ID"
                    value={notice.notice_id} onChange={e => n({ notice_id: e.target.value })}
                    disabled={!fieldsEditable}
                    data-testid="INP_lit_inst_notice_id" />
                </div>
                <ValidatedDatePicker vh={vh} name="notice_date" code={305} required
                  label="Date of Notice"
                  value={notice.notice_date} onChange={e => n({ notice_date: e.target.value })}
                  disabled={!fieldsEditable}
                  data-testid="INP_lit_inst_notice_date" />
                <ValidatedDatePicker vh={vh} name="due_date" code={305}
                  label="Date of First Response"
                  value={notice.due_date} onChange={e => n({ due_date: e.target.value })}
                  disabled={!fieldsEditable}
                  data-testid="INP_lit_inst_due_date" />
              </Section>

              {/* ── Statute & Demand ──────────────────────────────────── */}
              <Section title="Statute & Demand">
                <FormControl.SearchSelect label="Act / Law"
                  placeholder="— Select an Act —"
                  clearable
                  value={notice.law_id ?? null}
                  onChange={v => n({ law_id: v ? Number(v) : null })}
                  options={laws.map(l => ({ value: l.id, label: l.law_name }))}
                  disabled={!fieldsEditable}
                  data-testid="SEL_lit_inst_law" />
                <ValidatedInput vh={vh} name="section" code={109}
                  label="Section / Rule"
                  value={notice.section} onChange={e => n({ section: e.target.value })}
                  placeholder="e.g. Section 74"
                  disabled={!fieldsEditable}
                  data-testid="INP_lit_inst_section" />
                <FormControl.SearchSelect label="Jurisdiction"
                  placeholder="— Select a jurisdiction —"
                  clearable
                  value={notice.jurisdiction_id ?? null}
                  onChange={v => n({ jurisdiction_id: v ? Number(v) : null })}
                  options={jurisdictions.map(j => ({ value: j.id, label: j.name }))}
                  disabled={!fieldsEditable}
                  data-testid="SEL_lit_inst_jurisdiction" />
                <FormControl.Input label="Demand Amount (₹)"
                  value={notice.amount} onChange={e => n({ amount: e.target.value })}
                  placeholder="Optional"
                  disabled={!fieldsEditable}
                  data-testid="INP_lit_inst_amount" />
                <div className="col-span-2">
                  <ValidatedTextarea vh={vh} name="description" code={110} rows={2}
                    label="Description"
                    value={notice.description} onChange={e => n({ description: e.target.value })}
                    disabled={!fieldsEditable}
                    data-testid="TXT_lit_inst_description" />
                </div>
              </Section>

              {/* ── Structure ─────────────────────────────────────────── */}
              <Section title="Structure">
                <div className="col-span-2">
                  <FormControl.RadioGroup
                    label="Mode"
                    name="lit_wf_mode"
                    inline
                    disabled={!fieldsEditable}
                    value={workflow.is_free_style ? 'free' : 'template'}
                    onChange={v => { w({ is_free_style: v === 'free', proceeding_id: null }); setStructErr(null); }}
                    options={[
                      { value: 'template', label: 'Template-based' },
                      { value: 'free',     label: 'Free-style' },
                    ]}
                  />
                </div>
                {!workflow.is_free_style && (
                  <div className="col-span-2">
                    <FormControl.SearchSelect label="Proceeding Template"
                      required
                      placeholder="— Select a template —"
                      clearable
                      value={workflow.proceeding_id ?? null}
                      onChange={v => { w({ proceeding_id: v ? Number(v) : null }); setStructErr(null); }}
                      options={proceedings.map(p => ({
                        value: p.id,
                        label: `${p.name}${p.code ? ` (${p.code})` : ''}`,
                      }))}
                      disabled={!fieldsEditable}
                      data-testid="SEL_lit_inst_proceeding" />
                    {structErr && <p className="text-xs text-red-500 mt-1">{structErr}</p>}
                  </div>
                )}
              </Section>

              {/* ── Assignment ────────────────────────────────────────── */}
              <Section title="Assignment">
                {assignedDisplay && (
                  <div className="col-span-2 p-3 rounded-xl bg-indigo-50 border border-indigo-100 text-sm">
                    <p className="text-xs text-indigo-500 font-medium mb-0.5">Currently assigned to</p>
                    <p className="font-semibold text-indigo-800">{inst?.assigned_position_name}</p>
                    <p className="text-indigo-700">{inst?.assigned_user_name}</p>
                  </div>
                )}
                {fieldsEditable && (
                  <div className="col-span-2">
                    <p className="text-xs font-semibold text-gray-600 mb-2">
                      {inst?.assigned_position_id ? 'Reassign' : 'Assign'} to position
                      <span className="text-red-500"> *</span>
                    </p>
                    <LitPositionPicker
                      positionId={positionId}
                      onPositionChange={pos => {
                        setPositionId(pos?.id ?? null);
                        setResolvedPos(pos);
                        if (pos) setAssignErr(null);
                      }}
                    />
                    {assignErr && <p className="text-xs text-red-500 mt-1">{assignErr}</p>}
                  </div>
                )}
              </Section>
            </form>
          )}

          {/* ── HISTORY / EXECUTION TAB ─────────────────────────────── */}
          {activeTab === 'history' && instance && (
            historyLoading
              ? <div className="flex items-center gap-2 text-sm text-gray-400 p-4">
                  <Loader2 size={15} className="animate-spin" /> Loading history…
                </div>
              : <LitTimeline
                  instance={inst!}
                  history={history}
                  graph={graph}
                  canAdvance={canAct && isExecute}
                  onAdvance={handleAdvance}
                  mode={mode}
                />
          )}
        </Modal.Body>

        {activeTab === 'notice' && canEditNotice && (
          <Modal.Footer className={!isNew ? 'app-modal-footer--split' : 'justify-end'}>
            {!isNew && (
              <Button
                variant={(inst?.is_active ?? 1) === 1 ? 'inactive-outline' : 'active-outline'}
                icon={(inst?.is_active ?? 1) === 1 ? CircleMinus : CircleCheck}
                onClick={handleToggleActive}
                disabled={toggling || saving}
                data-testid="BTN_lit_inst_toggle_active">
                {toggling ? '...' : (inst?.is_active ?? 1) === 1 ? 'Inactive' : 'Active'}
              </Button>
            )}
            <div style={{ display: 'flex', gap: 8 }}>
              {/* View → Edit: existing record opens read-only, click to edit.
                  Rendered as a SEPARATE conditional (not a ternary in the same
                  slot) so the click can't land on a freshly-mounted submit
                  button and auto-fire the Update API. */}
              {!isNew && !isEditing && (
                <Button
                  variant="fill"
                  icon={Edit2}
                  onClick={() => setIsEditing(true)}
                  disabled={toggling || saving}
                  data-testid="BTN_lit_inst_edit">
                  Edit
                </Button>
              )}
              {/* Submit (create) or Update (existing + editing) */}
              {(isNew || isEditing) && (
                <Button type="submit" form="lit-notice-form"
                  variant={isNew ? 'fill' : 'update'}
                  icon={isNew ? Save : Check}
                  disabled={saving}
                  data-testid="BTN_lit_inst_save_notice">
                  {isNew ? 'Submit' : 'Update'}
                </Button>
              )}
            </div>
          </Modal.Footer>
        )}

        {activeTab === 'history' && !isNew && !isExecute && canAct && !isApproved && inst?.status !== 'Closed' && (() => {
          const isTerminal = !!graph?.steps.find(s => s.id === inst?.current_step_id)?.is_terminal;
          return (
            <Modal.Footer className="justify-start">
              <Button variant="delete-outline" icon={Lock} onClick={handleClose}
                disabled={!isTerminal}
                data-testid="BTN_lit_inst_close">
                Close Litigation
              </Button>
            </Modal.Footer>
          );
        })()}
      </Modal>

      <Toast toast={toast} onClose={() => setToast(null)} />
    </>
  );
};

export default LitInstanceDetail;
